import React, { useState, useRef, useEffect } from 'react';
import { CustomButton } from 'src/components/common/Buttons/CustomButton';
import Icon from 'src/components/common/Icons/Icons/Icon';

interface FileUploadModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (file: File) => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({ opened, onClose, onConfirm, buttonRef }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState<string>(`file-input-${Date.now()}`);
  const [isSelecting, setIsSelecting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Maximum file size: 5MB
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

  // Function to reset the file input
  const resetFileInput = () => {
    setInputKey(`file-input-${Date.now()}`);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setIsSelecting(false);
    
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File is too large. Maximum size is 5MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
        resetFileInput();
        return;
      }
      
      setSelectedFile(file);
      setFileType(file.type);
      
      // Create a preview URL for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleConfirm = () => {
    if (selectedFile) {
      onConfirm(selectedFile);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFileType('');
    setError(null);
    resetFileInput();
    setIsSelecting(false);
    onClose();
  };

  // Create a completely new file input element programmatically
  const openFilePicker = () => {
    // Create a new file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';
    
    // Set up change handler
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        const file = target.files[0];
        
        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          setError(`File is too large. Maximum size is 5MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
          return;
        }
        else{
          setError(null)
        }
        
        setSelectedFile(file);
        setFileType(file.type);
        
        // Create a preview URL for images
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = () => {
            setPreviewUrl(reader.result as string);
          };
          reader.readAsDataURL(file);
        } else {
          setPreviewUrl(null);
        }
      }
    };
    
    // Trigger the click event
    input.click();
  };

  if (!opened) return null;

  return (
    <div 
      ref={modalRef}
      className="absolute z-50 bottom-full right-0 w-full md:w-2/3 bg-white border border-gray-300 rounded-xl mb-2 shadow-lg" 
    >
      <div className="w-full bg-white rounded-xl shadow-xl border border-gray-300">
        {/* Close Button */}
        <div className="flex justify-end p-3">
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-800">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="px-6 pb-6">
          {error && (
            <div className="mb-4 text-xs text-red-600 text-center">
              {error}
            </div>
          )}

          {/* Upload Box / Preview */}
          {!selectedFile ? (
            <div 
              className="border-2 border-dashed border-gray-500 rounded-lg bg-gray-100 flex flex-col items-center justify-center p-6 text-center"
              // onClick={openFilePicker}
            >
              <p className="text-gray-800 text-sm">Upload document or image</p>
              <p className="text-xs text-gray-500 mt-2">Maximum file size: 5MB</p>
            </div>
          ) : previewUrl && fileType.startsWith('image/') ? (
            <div className="mb-4 flex justify-center">
              <img 
                src={previewUrl} 
                alt="Preview" 
                className="max-w-full max-h-48 object-contain rounded-md border" 
              />
            </div>
          ) : fileType === 'application/pdf' ? (
            <div className="mb-4 p-3 border rounded-lg bg-gray-50 flex items-center gap-2">
              <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm truncate">{selectedFile.name}</span>
            </div>
          ) : null}

          {/* Upload Trigger Button */}
          <button
            onClick={openFilePicker}
            className="flex items-center justify-center text-sm font-medium mt-4 hover:text-blue-800 transition"
          >
            <Icon name="upload" className="mr-2 ml-1" color="#4cb7ff" />
            <span>Upload documents or images from your system</span>
          </button>

          {/* Confirm Button */}
          <div className="flex justify-end mt-4">
            <button
              onClick={handleConfirm}
              className={`px-5 py-2 rounded-md text-sm font-medium transition ${
                selectedFile
                  ? 'bg-primary text-white hover:bg-slate-800'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              disabled={!selectedFile}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal; 